import React, { useEffect, useMemo, useState } from 'react';
import {
    CalendarCheck, ClipboardCheck, Scale, SlidersHorizontal, Calculator, Send,
    ShieldCheck, BookOpen,
} from 'lucide-react';
import moment from 'moment';

/**
 * Landing card shown when no year-end process exists yet for the
 * selected fiscal year — explains in plain Swedish what the flow does
 * and offers "Starta bokslut". Modern-card composition; brand-tinted
 * bullet icons follow the CategoryChip/stat-icon pattern (Assets).
 */
const AB_QUESTIONS = [
    // Grundkravet: bara MINDRE företag får använda K2 (ÅRL 1 kap. 3 §,
    // BFNAR 2016:10 p. 1.1) — gäller alla år, före 1.1A/1.1B-frågorna.
    ['hasSecuritiesOnRegulatedMarket', 'Företagets värdepapper är upptagna till handel på en reglerad marknad (eller motsvarande marknad utanför EES).'],
    ['exceededEmployees50BothYears', 'Medelantalet anställda har varit fler än 50 under vart och ett av de två senaste räkenskapsåren.'],
    ['exceededBalance40MkrBothYears', 'Balansomslutningen har varit större än 40 miljoner kronor för vart och ett av de två senaste räkenskapsåren.'],
    ['exceededTurnover80MkrBothYears', 'Nettoomsättningen har varit större än 80 miljoner kronor för vart och ett av de två senaste räkenskapsåren.'],
    // 'isPublikt' (tidigare isPublicCompany) frågas numera bland process-
    // grindarna nedan — samma fakta täcker både K2 1.1A a och ÅRL-grinden.
    ['isParentOfLargerGroup', 'Företaget är moderföretag i en större koncern.'],
    ['isParentPreparingConsolidatedAccounts', 'Företaget är moderföretag och upprättar koncernredovisning.'],
    ['hasForeignBranches', 'Företaget har eller har haft en eller flera filialer i utlandet under året.'],
    // 1.1A i: direkta innehav — indirekta (t.ex. certifikat) och enstaka
    // användning som betalningsmedel omfattas inte (BFN:s kommentar).
    ['hasDisqualifyingCryptoAssets', 'Företaget har eller har under året haft egna kryptotillgångar. (Indirekta innehav som certifikat räknas inte, inte heller att kryptovaluta enstaka gånger använts eller tagits emot som betalning.)'],
    ['hasShareBasedPurchases', 'Företaget har förvärvat varor eller tjänster mot aktierelaterad ersättning.'],
    ['hasCompoundFinancialInstruments', 'Företaget har emitterat konvertibla skuldebrev eller liknande sammansatta instrument.'],
    // K2 punkt 1.1B a och b är TVÅ skilda uteslutningar med egna undantag
    // (1.1C) — de var tidigare hopblandade i en fråga (fynd 2026-08-11).
    ['hasMaterialDeferredTaxLiability', 'Företaget har en väsentlig uppskjuten skatteskuld — skatt som beror på att tillgångar är bokförda högre än sitt skattemässiga värde, eller avsättningar/skulder lägre. (Obeskattade reserver som överavskrivningar och periodiseringsfonder räknas inte hit. Bedöms utifrån den senaste färdiga årsredovisningen.)'],
    ['hasBuildingGenerating75PctOfTurnover', 'Företaget har en eller flera byggnader som genererar minst 75 % av nettoomsättningen, t.ex. hyresintäkter. (En byggnad där verksamheten bara bedrivs — fabrik, restaurang, butik — räknas inte.)'],
];
const AB_11B_KEYS = ['hasMaterialDeferredTaxLiability', 'hasBuildingGenerating75PctOfTurnover'];
// K2 punkt 1.1C första stycket: 1.1B gäller inte om högst ETT av villkoren
// uppfylls; varje villkor avser VART OCH ETT av de två senaste räkenskapsåren.
const RELIEF_QUESTIONS = [
    ['exceededEmployees3BothYears', 'Har medelantalet anställda varit fler än 3 under vart och ett av de två senaste räkenskapsåren?'],
    ['exceededBalance15MkrBothYears', 'Har balansomslutningen varit större än 1,5 miljoner kronor för vart och ett av de två senaste räkenskapsåren?'],
    ['exceededTurnover3MkrBothYears', 'Har nettoomsättningen varit större än 3 miljoner kronor för vart och ett av de två senaste räkenskapsåren?'],
];
// Processgrindar (2026-08-12) — situationer guiden uttryckligen inte stödjer;
// servern stoppar ett ja innan processen startas (EligibilityService).
// Nej på avvecklingsfrågan är samtidigt attesten om fortsatt drift.
// Källor: ÅRL 5 kap. 23 § och 18 a §; ABL 25 kap. 30 §; BFNAR 2016:10 (K2)
// punkt 2.2; BFNAR 2017:3 (Årsbokslut) punkt 2.2. K1 (BFNAR 2006:1) saknar
// avvecklingsregeln — vid förenklat årsbokslut frågas den inte.
const AVVECKLING_KEY = 'harBeslutatAvveckla';
const AVVECKLING_LABEL = 'Har företaget beslutat att avveckla verksamheten?';
const AB_GATING_QUESTIONS = [
    ['isPublikt', 'Är bolaget ett publikt aktiebolag (publ)?'],
    ['isUnderLikvidationEllerKonkurs', 'Är bolaget i likvidation eller försatt i konkurs?'],
    [AVVECKLING_KEY, AVVECKLING_LABEL],
];
const avvecklingStopText = (companyType) => (
    'Ett företag som har beslutat att avveckla verksamheten ska ta hänsyn till beslutet '
    + 'när balans- och resultaträkningen upprättas '
    + `(${companyType === 'AB' ? 'K2 punkt 2.2' : 'BFNAR 2017:3 punkt 2.2'}) — värderingen `
    + 'ska då ske på annan grund än fortsatt drift, vilket guiden inte stödjer. '
    + 'Ta hjälp av en redovisningskonsult.'
);
const GATING_STOP_TEXTS = {
    isPublikt: 'Publika aktiebolag omfattas av ytterligare upplysningskrav i årsredovisningen '
        + '(bl.a. ÅRL 5 kap. 23 § och 18 a §) som guiden inte stödjer. Ta hjälp av en '
        + 'redovisningskonsult eller revisor.',
    isUnderLikvidationEllerKonkurs: 'Guiden bygger på att styrelsen upprättar årsredovisningen '
        + 'och att stämman fastställer den. I likvidation träder likvidatorn i styrelsens och '
        + 'den verkställande direktörens ställe (ABL 25 kap. 30 §), och bolag i likvidation '
        + 'eller konkurs stöds därför inte här. Ta hjälp av en redovisningskonsult.',
    [AVVECKLING_KEY]: avvecklingStopText('AB'),
};

/** Ja/Nej-segment i stället för nakna radioknappar — vald sida fylls i
 *  brandgrönt. value: true/false/undefined (obesvarad). */
const SegToggle = ({ value, onChange, name }) => (
    <span className="ye-seg" role="group" aria-label={name}>
        <button
            type="button"
            className={value === true ? 'active' : ''}
            aria-pressed={value === true}
            onClick={() => onChange(true)}
        >
            Ja
        </button>
        <button
            type="button"
            className={value === false ? 'active' : ''}
            aria-pressed={value === false}
            onClick={() => onChange(false)}
        >
            Nej
        </button>
    </span>
);

const YearEndIntroCard = ({ fiscalYear, eligibility, onStart, starting }) => {
    const [answers, setAnswers] = useState({});
    const set = (key, value) => setAnswers((current) => ({ ...current, [key]: value }));
    // One accounting regime per fiscal year (independent review 2026-09-05,
    // F8): an EF's year-end basis is the company's regime, the same fact the
    // asset module and the NE form use. The matching choice is preselected;
    // the other one explains that the regime is changed in settings, and the
    // backend refuses a mismatch regardless of what is clicked.
    const companyRegime = eligibility?.companyType === 'EF' && ['k1', 'k2'].includes(eligibility?.accountingRegime)
        ? eligibility.accountingRegime
        : null;
    useEffect(() => {
        if (!companyRegime) return;
        setAnswers((current) => (typeof current.useSimplified === 'boolean'
            ? current
            : { ...current, useSimplified: companyRegime === 'k1' }));
    }, [companyRegime]);
    // Vilka K2-frågor gäller? Backend (EligibilityService.describe) skickar
    // listan: 3 för äldre K2, 8 för reviderade K2. Ett förlängt första år som
    // börjar efter 2025-06-30 och slutar 2026-12-31 eller senare hamnar på
    // reviderade K2 (BFN:s övergångsregel) — då krävs alla 8 oavsett.
    const abQuestions = useMemo(() => {
        const facts = eligibility?.abFacts;
        const extendedFirstYear = answers.isFirstFiscalYear
            && (answers.operationsStartedAt || '') > '2025-06-30'
            && fiscalYear && moment(fiscalYear.end_date).format('YYYY-MM-DD') >= '2026-12-31';
        if (extendedFirstYear || !Array.isArray(facts) || !facts.length) return AB_QUESTIONS;
        return AB_QUESTIONS.filter(([key]) => facts.includes(key));
    }, [eligibility, answers.isFirstFiscalYear, answers.operationsStartedAt, fiscalYear]);
    const needsNormaltConfirm = eligibility?.requiresNormaltConfirmation
        && answers.useSimplified === true;
    // 1.1C-undantaget: visas när någon 1.1B-fråga besvarats med ja. Fort-
    // sättningsundantaget (K2 i fjol + normalt utanför 1.1B) gör storleks-
    // frågorna överflödiga — samma logik som backend (EligibilityService).
    const triggered11B = AB_11B_KEYS.some((key) => answers[key] === true)
        && abQuestions.some(([key]) => AB_11B_KEYS.includes(key));
    const continuationRelief = answers.appliedK2PreviousYear === true
        && answers.normallyNotCoveredBy11B === true;
    const sizeAnswered = RELIEF_QUESTIONS.every(([key]) => typeof answers[key] === 'boolean');
    const sizeRelief = sizeAnswered
        && RELIEF_QUESTIONS.filter(([key]) => answers[key] === true).length <= 1;
    const reliefResolved = !triggered11B || continuationRelief || sizeAnswered;
    const reliefGranted = !triggered11B || continuationRelief || sizeRelief;
    const ready = useMemo(() => {
        if (eligibility?.companyType === 'AB') {
            return abQuestions.every(([key]) => typeof answers[key] === 'boolean')
                && AB_GATING_QUESTIONS.every(([key]) => typeof answers[key] === 'boolean')
                && typeof answers.isFirstFiscalYear === 'boolean'
                && (!answers.isFirstFiscalYear || /^\d{4}-\d{2}-\d{2}$/.test(answers.operationsStartedAt || ''))
                && reliefResolved;
        }
        if (eligibility?.companyType === 'EF') {
            return typeof answers.useSimplified === 'boolean'
                && (!needsNormaltConfirm || answers.confirmedNormaltUnderGrans === true)
                // Avvecklingsfrågan gäller bara vanligt årsbokslut — K1
                // (BFNAR 2006:1) saknar regeln och frågas inte.
                && (answers.useSimplified !== false || typeof answers[AVVECKLING_KEY] === 'boolean');
        }
        if (eligibility?.companyType === 'HB' || eligibility?.companyType === 'KB') {
            return typeof answers[AVVECKLING_KEY] === 'boolean';
        }
        return true;
    }, [answers, eligibility, abQuestions, needsNormaltConfirm, reliefResolved]);
    const yearLabel = fiscalYear
        ? `${moment(fiscalYear.start_date).format('YYYY-MM-DD')} – ${moment(fiscalYear.end_date).format('YYYY-MM-DD')}`
        : '';
    const hasEnded = fiscalYear ? moment(fiscalYear.end_date).isBefore(moment(), 'day') : false;

    const bullets = [
        {
            Icon: ClipboardCheck,
            text: 'Vi kontrollerar först att allt är på plats — att momsen är redovisad, banken är avstämd och att bokföringen går ihop.',
        },
        {
            Icon: Scale,
            text: 'Du går igenom och godkänner saldot på dina konton, så att du vet att siffrorna stämmer.',
        },
        {
            Icon: SlidersHorizontal,
            text: 'Vi hjälper dig med bokslutshändelser — till exempel att flytta intäkter och kostnader till rätt år.',
        },
        {
            Icon: Calculator,
            text: 'Årets skatt beräknas och årets resultat bokförs, med tydliga förklaringar hela vägen.',
        },
        {
            Icon: Send,
            text: 'Till sist får du årsredovisning och deklaration färdiga att lämna in till myndigheterna.',
        },
    ];

    return (
        <>
            <style>{`
                .ye-intro-lead {
                    color: #4b5563;
                    font-size: 0.875rem;
                    line-height: 1.6;
                }
                .ye-intro-bullets {
                    list-style: none;
                    padding: 0;
                    margin: 0 0 1.5rem 0;
                }
                .ye-intro-bullets li {
                    display: flex;
                    align-items: center;
                    gap: 0.85rem;
                    padding: 0.6rem 0;
                    color: #4b5563;
                    font-size: 0.875rem;
                    line-height: 1.5;
                }
                .ye-intro-bullet-icon {
                    flex-shrink: 0;
                    width: 34px;
                    height: 34px;
                    border-radius: 9px;
                    background: var(--rgba-primary-1);
                    color: var(--primary);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .ye-intro-footnote {
                    color: #9ca3af;
                    font-size: 0.8125rem;
                    margin: 0.75rem 0 0 0;
                }

                /* Behörighetspanelen — modern-card-språk, inga grå sjok. */
                .ye-elig {
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    margin-bottom: 1.25rem;
                    overflow: hidden;
                }
                .ye-elig-head {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 0.9rem 1.25rem;
                    background: var(--rgba-primary-1);
                }
                .ye-elig-head-icon {
                    flex-shrink: 0;
                    width: 34px;
                    height: 34px;
                    border-radius: 9px;
                    background: #fff;
                    color: var(--primary);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .ye-elig-title { font-weight: 600; font-size: 0.9rem; color: var(--title, #374151); }
                .ye-elig-sub { font-size: 0.78rem; color: #6b7280; margin-top: 1px; }
                .ye-elig-row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 1rem;
                    padding: 0.7rem 1.25rem;
                    border-top: 1px solid #f3f4f6;
                    font-size: 0.85rem;
                    color: #374151;
                    line-height: 1.45;
                }
                .ye-elig-row:first-of-type { border-top: none; }
                .ye-seg {
                    display: inline-flex;
                    flex-shrink: 0;
                    border: 1px solid #e5e7eb;
                    border-radius: 999px;
                    overflow: hidden;
                    background: #fff;
                }
                .ye-seg button {
                    border: none;
                    background: transparent;
                    padding: 0.28rem 1.05rem;
                    font-size: 0.8125rem;
                    font-weight: 600;
                    color: #9ca3af;
                    cursor: pointer;
                    transition: background 0.15s ease, color 0.15s ease;
                }
                .ye-seg button + button { border-left: 1px solid #e5e7eb; }
                .ye-seg button:hover { color: var(--primary); }
                .ye-seg button.active {
                    background: var(--primary);
                    color: #fff;
                }
                .ye-elig-date-row { border-top: 1px solid #f3f4f6; padding: 0.7rem 1.25rem; }
                .ye-elig-date-row label {
                    font-size: 0.85rem; color: #374151; display: flex;
                    align-items: center; gap: 0.75rem; margin: 0;
                }
                .ye-elig-date-row input[type="date"] {
                    border: 1px solid #e5e7eb; border-radius: 8px;
                    padding: 0.35rem 0.6rem; font-size: 0.85rem; color: #374151;
                }
                .ye-elig-date-row input[type="date"]:focus {
                    outline: none; border-color: var(--primary);
                    box-shadow: 0 0 0 3px var(--rgba-primary-1);
                }
                .ye-choice-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
                    gap: 0.75rem;
                    padding: 0.9rem 1.25rem;
                }
                .ye-choice-card {
                    border: 1px solid #e5e7eb;
                    border-radius: 10px;
                    background: #fff;
                    text-align: left;
                    padding: 0.85rem 1rem;
                    cursor: pointer;
                    transition: border-color 0.15s ease, box-shadow 0.15s ease;
                }
                .ye-choice-card:hover { border-color: var(--primary); }
                .ye-choice-card.selected {
                    border-color: var(--primary);
                    box-shadow: 0 0 0 3px var(--rgba-primary-1);
                }
                .ye-choice-label { font-weight: 600; font-size: 0.875rem; color: var(--title, #374151); }
                .ye-choice-help { font-size: 0.78rem; color: #6b7280; margin-top: 2px; }
                .ye-elig-warning {
                    margin: 0 1.25rem 1rem;
                    font-size: 0.8125rem;
                }
                .ye-elig-warning input[type="checkbox"] { accent-color: var(--primary); }
            `}</style>

            <div className="modern-card">
                <div className="modern-card-header">
                    <div>
                        <h3 className="section-title">
                            <CalendarCheck size={20} />
                            Dags för bokslut
                        </h3>
                        <p className="section-subtitle">
                            Räkenskapsåret {yearLabel}
                        </p>
                    </div>
                </div>
                <div className="modern-card-body">
                    <p className="ye-intro-lead">
                        Ett bokslut är den årliga summeringen av företagets bokföring — det som visar
                        hur året gick och vad företaget äger och är skyldigt. Den här guiden tar dig
                        genom hela processen, steg för steg, i lugn takt och på vanlig svenska:
                    </p>

                    <ul className="ye-intro-bullets">
                        {bullets.map(({ Icon, text }, i) => (
                            <li key={i}>
                                <span className="ye-intro-bullet-icon"><Icon size={16} /></span>
                                <span>{text}</span>
                            </li>
                        ))}
                    </ul>

                    {eligibility?.companyType === 'AB' && (
                        <div className="ye-elig">
                            <div className="ye-elig-head">
                                <span className="ye-elig-head-icon"><ClipboardCheck size={18} /></span>
                                <div>
                                    <div className="ye-elig-title">Företagets situation</div>
                                    <div className="ye-elig-sub">
                                        Guiden stödjer inte alla situationer — ett ja här betyder att
                                        bokslutet i stället behöver göras med hjälp av en rådgivare.
                                    </div>
                                </div>
                            </div>
                            {AB_GATING_QUESTIONS.map(([key, label]) => (
                                <React.Fragment key={key}>
                                    <div className="ye-elig-row">
                                        <span>{label}</span>
                                        <SegToggle
                                            value={answers[key]}
                                            onChange={(v) => set(key, v)}
                                            name={label}
                                        />
                                    </div>
                                    {answers[key] === true && (
                                        <div className="alert alert-warning ye-elig-warning">
                                            {GATING_STOP_TEXTS[key]}
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                    {eligibility?.companyType === 'AB' && (
                        <div className="ye-elig">
                            <div className="ye-elig-head">
                                <span className="ye-elig-head-icon"><ShieldCheck size={18} /></span>
                                <div>
                                    <div className="ye-elig-title">Kontroll av K2-behörighet</div>
                                    <div className="ye-elig-sub">
                                        Svara på frågorna nedan — ett ja kan innebära att bolaget måste
                                        använda K3 i stället, och då stoppar vi innan något blir fel.
                                    </div>
                                </div>
                            </div>
                            {abQuestions.map(([key, label]) => (
                                <div className="ye-elig-row" key={key}>
                                    <span>{label}</span>
                                    <SegToggle
                                        value={answers[key]}
                                        onChange={(v) => set(key, v)}
                                        name={label}
                                    />
                                </div>
                            ))}
                            {triggered11B && (
                                <div className="alert alert-warning ye-elig-warning">
                                    <strong>Undantag för mindre företag (K2 punkt 1.1C).</strong>{' '}
                                    Även med svaret ovan får de minsta företagen ändå använda K2.
                                    Svara på storleksfrågorna — gäller högst ett av villkoren får
                                    K2 användas.
                                    {RELIEF_QUESTIONS.map(([key, label]) => (
                                        <div className="ye-elig-row" key={key}>
                                            <span>{label}</span>
                                            <SegToggle
                                                value={answers[key]}
                                                onChange={(v) => set(key, v)}
                                                name={label}
                                            />
                                        </div>
                                    ))}
                                    {sizeAnswered && !sizeRelief && (
                                        <>
                                            <div className="mt-2">
                                                Storleksundantaget gäller inte. Ett företag som redan
                                                tillämpade K2 föregående år får ändå fortsätta, om det
                                                normalt inte omfattas av begränsningen.
                                            </div>
                                            <div className="ye-elig-row">
                                                <span>Tillämpade företaget K2 redan föregående räkenskapsår?</span>
                                                <SegToggle
                                                    value={answers.appliedK2PreviousYear}
                                                    onChange={(v) => set('appliedK2PreviousYear', v)}
                                                    name="K2 föregående år"
                                                />
                                            </div>
                                            <div className="ye-elig-row">
                                                <span>Är situationen tillfällig — omfattas företaget normalt inte av begränsningen?</span>
                                                <SegToggle
                                                    value={answers.normallyNotCoveredBy11B}
                                                    onChange={(v) => set('normallyNotCoveredBy11B', v)}
                                                    name="Normalt utanför begränsningen"
                                                />
                                            </div>
                                        </>
                                    )}
                                    {reliefResolved && (
                                        <div className="mt-2">
                                            {reliefGranted
                                                ? 'Undantaget gäller — K2 kan användas.'
                                                : 'Inget undantag gäller — bolaget behöver K3, och vi stoppar innan något blir fel.'}
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className="ye-elig-row">
                                <span>Är detta företagets första räkenskapsår?</span>
                                <SegToggle
                                    value={answers.isFirstFiscalYear}
                                    onChange={(v) => set('isFirstFiscalYear', v)}
                                    name="Första räkenskapsår"
                                />
                            </div>
                            {answers.isFirstFiscalYear && (
                                <div className="ye-elig-date-row">
                                    <label>
                                        Verksamheten började
                                        <input
                                            type="date"
                                            value={answers.operationsStartedAt || ''}
                                            onChange={(e) => set('operationsStartedAt', e.target.value)}
                                        />
                                    </label>
                                </div>
                            )}
                        </div>
                    )}
                    {eligibility?.companyType === 'EF' && (
                        <div className="ye-elig">
                            <div className="ye-elig-head">
                                <span className="ye-elig-head-icon"><BookOpen size={18} /></span>
                                <div>
                                    <div className="ye-elig-title">Välj typ av årsbokslut</div>
                                    <div className="ye-elig-sub">
                                        Beräknad nettoomsättning i år:{' '}
                                        {Number(eligibility.netTurnover || 0).toLocaleString('sv-SE')} kr ·
                                        gräns för förenklat årsbokslut:{' '}
                                        {Number(eligibility.k1Limit).toLocaleString('sv-SE')} kr.
                                    </div>
                                </div>
                            </div>
                            <div className="ye-choice-grid">
                                <button
                                    type="button"
                                    className={`ye-choice-card${answers.useSimplified === true ? ' selected' : ''}`}
                                    disabled={companyRegime === 'k2'}
                                    title={companyRegime === 'k2' ? 'Företagets regelverk är K2 enligt företagsinställningarna' : undefined}
                                    // Vid K1 rensas avvecklingssvaret — frågan hör bara till
                                    // vanligt årsbokslut och ska inte följa med i bedömningen.
                                    onClick={() => setAnswers((current) => {
                                        const { [AVVECKLING_KEY]: dropped, ...rest } = current;
                                        return { ...rest, useSimplified: true };
                                    })}
                                    aria-pressed={answers.useSimplified === true}
                                >
                                    <div className="ye-choice-label">Förenklat årsbokslut (K1)</div>
                                    <div className="ye-choice-help">
                                        För verksamheter som normalt omsätter högst{' '}
                                        {Number(eligibility.k1Limit).toLocaleString('sv-SE')} kr — enklare
                                        uppställning, mindre att fylla i.
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    className={`ye-choice-card${answers.useSimplified === false ? ' selected' : ''}`}
                                    disabled={companyRegime === 'k1'}
                                    title={companyRegime === 'k1' ? 'Företagets regelverk är K1 enligt företagsinställningarna' : undefined}
                                    onClick={() => set('useSimplified', false)}
                                    aria-pressed={answers.useSimplified === false}
                                >
                                    <div className="ye-choice-label">Vanligt årsbokslut</div>
                                    <div className="ye-choice-help">
                                        Fullständigt årsbokslut — krävs när omsättningen normalt är över
                                        gränsen, och går alltid att välja frivilligt.
                                    </div>
                                </button>
                            </div>
                            {companyRegime && (
                                <div className="ye-elig-sub" style={{ marginTop: '0.5rem' }}>
                                    Typen av årsbokslut följer företagets redovisningsregelverk
                                    ({companyRegime === 'k1' ? 'K1, förenklat årsbokslut' : 'K2, vanligt årsbokslut'}) — samma
                                    regelverk som tillgångsmodulen och NE-bilagan använder. Vill du byta gör du det under{' '}
                                    <a href="../settings/company">Inställningar → Företag</a> innan bokslutet startas.
                                </div>
                            )}
                            {needsNormaltConfirm && (
                                <div className="alert alert-warning ye-elig-warning">
                                    Årets nettoomsättning överstiger {Number(eligibility.k1Limit).toLocaleString('sv-SE')} kr.
                                    Förenklat årsbokslut får ändå användas om omsättningen <em>normalt</em> är
                                    högst {Number(eligibility.k1Limit).toLocaleString('sv-SE')} kr
                                    (6 kap. 3 § bokföringslagen) — bedömningen är din.
                                    <label className="d-block mt-2 mb-0">
                                        <input
                                            type="checkbox"
                                            checked={answers.confirmedNormaltUnderGrans === true}
                                            onChange={(e) => set('confirmedNormaltUnderGrans', e.target.checked)}
                                        />{' '}
                                        Jag bekräftar att årets omsättning är ett undantag och att den normalt
                                        är högst {Number(eligibility.k1Limit).toLocaleString('sv-SE')} kr.
                                    </label>
                                </div>
                            )}
                            {answers.useSimplified === false && (
                                <>
                                    <div className="ye-elig-row">
                                        <span>{AVVECKLING_LABEL}</span>
                                        <SegToggle
                                            value={answers[AVVECKLING_KEY]}
                                            onChange={(v) => set(AVVECKLING_KEY, v)}
                                            name={AVVECKLING_LABEL}
                                        />
                                    </div>
                                    {answers[AVVECKLING_KEY] === true && (
                                        <div className="alert alert-warning ye-elig-warning">
                                            {avvecklingStopText('EF')}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                    {(eligibility?.companyType === 'HB' || eligibility?.companyType === 'KB') && (
                        <div className="ye-elig">
                            <div className="ye-elig-head">
                                <span className="ye-elig-head-icon"><ClipboardCheck size={18} /></span>
                                <div>
                                    <div className="ye-elig-title">Företagets situation</div>
                                    <div className="ye-elig-sub">
                                        Guiden stödjer inte alla situationer — ett ja här betyder att
                                        bokslutet i stället behöver göras med hjälp av en rådgivare.
                                    </div>
                                </div>
                            </div>
                            <div className="ye-elig-row">
                                <span>{AVVECKLING_LABEL}</span>
                                <SegToggle
                                    value={answers[AVVECKLING_KEY]}
                                    onChange={(v) => set(AVVECKLING_KEY, v)}
                                    name={AVVECKLING_LABEL}
                                />
                            </div>
                            {answers[AVVECKLING_KEY] === true && (
                                <div className="alert alert-warning ye-elig-warning">
                                    {avvecklingStopText('HB')}
                                </div>
                            )}
                        </div>
                    )}

                    {!hasEnded && fiscalYear && (
                        <div className="alert alert-warning" style={{ fontSize: '0.875rem' }}>
                            Räkenskapsåret slutar först den {moment(fiscalYear.end_date).format('YYYY-MM-DD')}.
                            Bokslutet kan startas när året har tagit slut.
                        </div>
                    )}

                    <button
                        className="btn btn-primary"
                        onClick={() => onStart(answers)}
                        disabled={starting || !ready}
                    >
                        {starting ? 'Startar…' : 'Starta bokslut'}
                    </button>
                    <p className="ye-intro-footnote">
                        Du kan pausa när som helst — allt du gör sparas, och varje steg går att ångra.
                    </p>
                </div>
            </div>
        </>
    );
};

export default YearEndIntroCard;
